import React, { useState, useRef } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { useParams } from 'react-router-dom';
import SEO from '../../components/common/SEO';
import { useTranslation } from 'react-i18next';
import emailjs from '@emailjs/browser';
import {
  FaPlay, FaCheck, FaMobileAlt, FaBoxOpen, FaShoppingCart,
  FaBullhorn, FaPlug, FaSignInAlt, FaCalendarAlt, FaArrowRight,
  FaTimes, FaTimesCircle, FaFileContract, FaCheckCircle
} from 'react-icons/fa';
import theme from '../../theme';

import belenGemuseLogo from '../../assets/images/references/belen_gemuse.png';
import cbGrosshandelLogo from '../../assets/images/references/cbgrosshandel.png';
import dosteliLogo from '../../assets/images/references/dosteli_food.png';
import pasaLogo from '../../assets/images/references/pasa_lebensmittel.png';

const EMAILJS_SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID;
const EMAILJS_TEMPLATE_ID = process.env.REACT_APP_EMAILJS_DEMO_TEMPLATE_ID;
const EMAILJS_PUBLIC_KEY = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;

const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(24px); }
  to { opacity: 1; transform: translateY(0); }
`;
const pulse = keyframes`
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.05); }
`;
const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;
const scaleIn = keyframes`
  from { opacity: 0; transform: scale(0.92); }
  to { opacity: 1; transform: scale(1); }
`;

const PageContainer = styled.div`min-height: 100vh;`;

const HeroSection = styled.section`
  position: relative;
  padding: 140px 5% 100px;
  background: linear-gradient(160deg, #0f172a 0%, #1e293b 60%, ${theme.colors.primary} 100%);
  text-align: center;
  color: white;
  overflow: hidden;
  &::before {
    content: '';
    position: absolute;
    top: -50%; right: -20%;
    width: 800px; height: 800px;
    background: radial-gradient(circle, rgba(231,76,60,0.08) 0%, transparent 70%);
    border-radius: 50%;
  }
`;
const HeroInner = styled.div`
  position: relative; z-index: 2;
  max-width: 800px; margin: 0 auto;
  animation: ${fadeInUp} 0.7s ease-out;
`;
const HeroBadge = styled.span`
  display: inline-flex; align-items: center; gap: 0.5rem;
  background: rgba(231,76,60,0.15); color: ${theme.colors.primary};
  padding: 0.5rem 1.25rem; border-radius: 50px;
  font-size: 0.72rem; font-weight: 800; letter-spacing: 2px;
  margin-bottom: 1.5rem; border: 1px solid rgba(231,76,60,0.25);
  text-transform: uppercase;
`;
const HeroTitle = styled.h1`
  font-size: 3.5rem; font-weight: 800; line-height: 1.1; margin-bottom: 1rem;
  @media (max-width: ${theme.breakpoints.tablet}) { font-size: 2.5rem; }
  @media (max-width: ${theme.breakpoints.mobile}) { font-size: 2rem; }
`;
const HeroSubtitle = styled.p`
  font-size: 1.2rem; opacity: 0.8; line-height: 1.6;
  max-width: 550px; margin: 0 auto 2.5rem;
  @media (max-width: ${theme.breakpoints.mobile}) { font-size: 1rem; }
`;
const HeroButtons = styled.div`
  display: flex; gap: 1rem; justify-content: center; flex-wrap: wrap; margin-bottom: 2rem;
`;
const HeroBtn = styled.button`
  display: inline-flex; align-items: center; gap: 0.6rem;
  padding: 0.9rem 1.75rem; border-radius: 50px;
  font-size: 0.95rem; font-weight: 700; cursor: pointer;
  transition: all 0.3s ease; border: none;
  ${props => props.$primary && css`
    background: ${theme.colors.primary}; color: white;
    box-shadow: 0 4px 20px rgba(231,76,60,0.35);
    &:hover { background: #c0392b; transform: translateY(-2px); }
  `}
  ${props => props.$secondary && css`
    background: rgba(255,255,255,0.1); color: white;
    border: 1.5px solid rgba(255,255,255,0.25); backdrop-filter: blur(10px);
    &:hover { background: rgba(255,255,255,0.18); border-color: rgba(255,255,255,0.45); transform: translateY(-2px); }
  `}
  ${props => props.$outline && css`
    background: transparent; color: white;
    border: 1.5px solid rgba(255,255,255,0.2);
    &:hover { border-color: rgba(255,255,255,0.5); background: rgba(255,255,255,0.06); transform: translateY(-2px); }
  `}
  svg { font-size: 0.85rem; }
  @media (max-width: ${theme.breakpoints.mobile}) { padding: 0.8rem 1.4rem; font-size: 0.88rem; }
`;
const HeroTrust = styled.div`
  display: flex; align-items: center; justify-content: center; gap: 1.5rem; flex-wrap: wrap;
`;
const HeroTrustItem = styled.span`
  display: flex; align-items: center; gap: 0.4rem;
  font-size: 0.78rem; font-weight: 600; color: rgba(255,255,255,0.55);
  svg { font-size: 0.65rem; }
`;

const SectionBadge = styled.span`
  display: inline-flex; align-items: center; gap: 0.4rem;
  background: rgba(231,76,60,0.08); color: ${theme.colors.primary};
  padding: 0.4rem 1rem; border-radius: 50px;
  font-size: 0.7rem; font-weight: 800; letter-spacing: 2px;
  margin-bottom: 1rem; text-transform: uppercase;
`;
const SectionTitle = styled.h2`
  font-size: 2.25rem; font-weight: 800; color: ${theme.colors.secondary};
  margin-bottom: 0.75rem; line-height: 1.2;
  @media (max-width: ${theme.breakpoints.mobile}) { font-size: 1.7rem; }
`;
const SectionSubtitle = styled.p`
  font-size: 1rem; color: ${theme.colors.darkGray};
  max-width: 550px; line-height: 1.6; margin-bottom: 2.5rem;
`;

const VideoSection = styled.section`padding: 80px 5%; background: #ffffff;`;
const VideoInner = styled.div`max-width: 1000px; margin: 0 auto;`;
const VideoGrid = styled.div`
  display: grid; grid-template-columns: 1.4fr 1fr; gap: 3rem; align-items: center;
  @media (max-width: ${theme.breakpoints.tablet}) { grid-template-columns: 1fr; gap: 2rem; }
`;
const VideoEmbed = styled.div`
  position: relative; border-radius: 16px; overflow: hidden;
  background: #0f172a; aspect-ratio: 16/9;
  display: flex; align-items: center; justify-content: center;
  box-shadow: 0 20px 50px rgba(0,0,0,0.12);
  cursor: pointer; transition: all 0.3s ease;
  &:hover { transform: translateY(-4px); box-shadow: 0 24px 60px rgba(0,0,0,0.18); }
`;
const PlayButton = styled.div`
  width: 72px; height: 72px; border-radius: 50%;
  background: ${theme.colors.primary};
  display: flex; align-items: center; justify-content: center;
  color: white; font-size: 1.5rem;
  box-shadow: 0 8px 30px rgba(231,76,60,0.4);
  animation: ${pulse} 2s ease-in-out infinite;
  svg { margin-left: 4px; }
`;
const VideoPlaceholderText = styled.p`
  position: absolute; bottom: 1.5rem; left: 0; right: 0;
  text-align: center; color: rgba(255,255,255,0.4);
  font-size: 0.85rem; font-weight: 600;
`;
const VideoFeatures = styled.div`display: flex; flex-direction: column; gap: 1rem;`;
const VideoFeatureItem = styled.div`
  display: flex; align-items: center; gap: 0.85rem;
  padding: 0.85rem 1.25rem; background: #f8f9fa;
  border-radius: 12px; border: 1px solid #e5e7eb;
  transition: all 0.25s ease;
  &:hover { border-color: ${theme.colors.primary}; box-shadow: 0 4px 12px rgba(231,76,60,0.08); transform: translateX(4px); }
`;
const FeatureIcon = styled.div`
  width: 36px; height: 36px; border-radius: 10px;
  background: rgba(231,76,60,0.1);
  display: flex; align-items: center; justify-content: center; flex-shrink: 0;
  svg { font-size: 0.85rem; color: ${theme.colors.primary}; }
`;
const FeatureText = styled.span`font-size: 0.92rem; font-weight: 600; color: ${theme.colors.secondary};`;

/* ─── MODAL (FULL FORM) ─── */
const ModalOverlay = styled.div`
  position: fixed; top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(0,0,0,0.6); backdrop-filter: blur(4px);
  z-index: 9999; display: flex; align-items: center; justify-content: center;
  padding: 1rem; animation: ${fadeIn} 0.2s ease-out;
`;
const ModalCard = styled.div`
  background: white; border-radius: 20px; padding: 2.5rem;
  max-width: 600px; width: 100%; position: relative;
  animation: ${scaleIn} 0.3s ease-out;
  box-shadow: 0 24px 64px rgba(0,0,0,0.2);
  max-height: 90vh; overflow-y: auto;
  @media (max-width: ${theme.breakpoints.mobile}) { padding: 2rem 1.5rem; }
`;
const ModalClose = styled.button`
  position: absolute; top: 1rem; right: 1rem;
  background: none; border: none; cursor: pointer;
  color: #94a3b8; font-size: 1.1rem; padding: 4px;
  transition: color 0.2s; z-index: 1;
  &:hover { color: ${theme.colors.secondary}; }
`;


/* ─── SOCIAL PROOF ─── */
const ProofSection = styled.section`
  padding: 60px 5%; background: #fff;
  border-top: 1px solid #f0f0f0; border-bottom: 1px solid #f0f0f0;
`;
const ProofInner = styled.div`max-width: 800px; margin: 0 auto; text-align: center;`;
const ProofTitle = styled.h3`font-size: 1.25rem; font-weight: 700; color: ${theme.colors.secondary}; margin-bottom: 0.5rem;`;
const ProofSubtitle = styled.p`font-size: 0.9rem; color: ${theme.colors.darkGray}; margin-bottom: 2rem;`;
const LogoRow = styled.div`
  display: flex; align-items: center; justify-content: center; gap: 3rem; flex-wrap: wrap; opacity: 0.6;
  img { height: 48px; max-width: 130px; object-fit: contain; filter: grayscale(100%); transition: all 0.3s ease; &:hover { filter: grayscale(0%); opacity: 1; } }
  @media (max-width: ${theme.breakpoints.mobile}) { gap: 2rem; img { height: 36px; max-width: 100px; } }
`;

/* ─── BOOK SECTION (CALENDLY ONLY) ─── */
const BookSection = styled.section`
  padding: 100px 5%;
  background: linear-gradient(160deg, #0f172a 0%, #1e293b 100%);
  text-align: center; color: white; position: relative; overflow: hidden;
  &::before {
    content: ''; position: absolute; top: -40%; right: -15%;
    width: 500px; height: 500px;
    background: radial-gradient(circle, rgba(231,76,60,0.1) 0%, transparent 60%);
    border-radius: 50%;
  }
`;
const BookInner = styled.div`max-width: 700px; margin: 0 auto; position: relative; z-index: 2;`;
const BookBadge = styled.span`
  display: inline-flex; align-items: center; gap: 0.4rem;
  background: rgba(231,76,60,0.15); color: ${theme.colors.primary};
  padding: 0.4rem 1rem; border-radius: 50px;
  font-size: 0.7rem; font-weight: 800; letter-spacing: 2px;
  margin-bottom: 1.25rem; text-transform: uppercase;
  border: 1px solid rgba(231,76,60,0.2);
`;
const BookTitle = styled.h2`
  font-size: 2.5rem; font-weight: 800; margin-bottom: 1rem; line-height: 1.15;
  @media (max-width: ${theme.breakpoints.mobile}) { font-size: 1.8rem; }
`;
const BookSubtitle = styled.p`
  font-size: 1.05rem; color: rgba(255,255,255,0.7);
  line-height: 1.7; max-width: 550px; margin: 0 auto 2rem;
`;
const ApproachRow = styled.div`
  display: flex; align-items: center; justify-content: center; gap: 2rem;
  margin-bottom: 2.5rem; flex-wrap: wrap;
`;
const ApproachChip = styled.div`
  display: flex; align-items: center; gap: 0.5rem;
  font-size: 0.88rem; font-weight: 600; color: rgba(255,255,255,0.8);
  svg { font-size: 0.7rem; color: ${theme.colors.primary}; }
`;
const CalendlyButton = styled.a`
  display: inline-flex; align-items: center; gap: 0.65rem;
  background: ${theme.colors.primary}; color: white;
  padding: 1.1rem 2.75rem; border-radius: 50px;
  font-size: 1.1rem; font-weight: 700; text-decoration: none;
  transition: all 0.3s ease; box-shadow: 0 4px 20px rgba(231,76,60,0.35);
  &:hover { background: #c0392b; transform: translateY(-3px); box-shadow: 0 8px 32px rgba(231,76,60,0.45); }
  svg { font-size: 0.9rem; }
`;
const CalendlyNote = styled.p`font-size: 0.82rem; color: rgba(255,255,255,0.45); margin-top: 1.25rem;`;

/* ─── FORM COMPONENTS (used in modal) ─── */
const FormTitle = styled.h3`font-size: 1.25rem; font-weight: 700; color: ${theme.colors.secondary}; margin-bottom: 0.4rem;`;
const FormSubtitle = styled.p`font-size: 0.88rem; color: ${theme.colors.darkGray}; margin-bottom: 1.75rem; line-height: 1.5;`;
const FormRow = styled.div`
  display: grid;
  grid-template-columns: ${props => props.$cols === 2 ? '1fr 1fr' : '1fr'};
  gap: 1rem; margin-bottom: 1rem;
  @media (max-width: ${theme.breakpoints.mobile}) { grid-template-columns: 1fr; }
`;
const FormGroup = styled.div``;
const Label = styled.label`
  display: block; font-size: 0.82rem; font-weight: 600;
  color: ${theme.colors.secondary}; margin-bottom: 0.4rem;
  span { color: ${theme.colors.primary}; }
`;
const Input = styled.input`
  width: 100%; padding: 0.7rem 1rem; border: 1.5px solid #e2e8f0;
  border-radius: 10px; font-size: 0.9rem; transition: all 0.2s; background: #f8fafc;
  &:focus { outline: none; border-color: ${theme.colors.primary}; background: white; box-shadow: 0 0 0 3px rgba(231,76,60,0.08); }
`;
const Select = styled.select`
  width: 100%; padding: 0.7rem 1rem; border: 1.5px solid #e2e8f0;
  border-radius: 10px; font-size: 0.9rem; transition: all 0.2s;
  background: #f8fafc; cursor: pointer;
  &:focus { outline: none; border-color: ${theme.colors.primary}; background: white; box-shadow: 0 0 0 3px rgba(231,76,60,0.08); }
`;
const TextArea = styled.textarea`
  width: 100%; padding: 0.7rem 1rem; border: 1.5px solid #e2e8f0;
  border-radius: 10px; font-size: 0.9rem; min-height: 80px; resize: vertical; background: #f8fafc;
  &:focus { outline: none; border-color: ${theme.colors.primary}; background: white; box-shadow: 0 0 0 3px rgba(231,76,60,0.08); }
`;
const FieldHint = styled.p`font-size: 0.72rem; color: #94a3b8; margin-top: 0.3rem;`;
const SubmitButton = styled.button`
  width: 100%; padding: 0.9rem; background: ${theme.colors.primary};
  color: white; border: none; border-radius: 50px;
  font-size: 1rem; font-weight: 700; cursor: pointer;
  display: flex; align-items: center; justify-content: center; gap: 0.5rem;
  transition: all 0.3s ease; margin-top: 0.5rem;
  &:hover:not(:disabled) { background: #c0392b; transform: translateY(-2px); box-shadow: 0 8px 24px rgba(231,76,60,0.35); }
  &:disabled { opacity: 0.6; cursor: not-allowed; }
  svg { font-size: 0.85rem; }
`;
const SuccessCard = styled.div`
  text-align: center; padding: 2rem;
  svg { font-size: 3rem; color: #10b981; margin-bottom: 1rem; }
  h3 { font-size: 1.25rem; color: ${theme.colors.secondary}; margin-bottom: 0.5rem; }
  p { color: ${theme.colors.darkGray}; line-height: 1.6; }
`;
const ErrorMessage = styled.div`
  background: #fef2f2; border: 1px solid #fecaca; color: #991b1b;
  padding: 1rem 1.25rem; border-radius: 10px; margin-bottom: 1.5rem; font-size: 0.9rem;
`;
const TrustBadgesRow = styled.div`
  display: flex; align-items: center; justify-content: center; gap: 1.5rem;
  margin-top: 1.25rem; flex-wrap: wrap;
  @media (max-width: ${theme.breakpoints.mobile}) { gap: 0.75rem; }
`;
const TrustBadge = styled.div`
  display: flex; align-items: center; gap: 0.4rem;
  font-size: 0.78rem; font-weight: 600; color: #64748b;
  svg { font-size: 0.7rem; color: ${theme.colors.primary}; flex-shrink: 0; }
`;

const featureIcons = [
  <FaSignInAlt />, <FaBoxOpen />, <FaShoppingCart />,
  <FaBullhorn />, <FaMobileAlt />, <FaPlug />
];

const DemoPage = () => {
  const { t } = useTranslation();
  const { lang } = useParams();
  const videoRef = useRef(null);
  const bookRef = useRef(null);

  const [showFormModal, setShowFormModal] = useState(false);
  const [videoUnlocked, setVideoUnlocked] = useState(false);

  const [formData, setFormData] = useState({
    companyName: '', contactName: '', email: '', phone: '',
    country: '', countryOther: '', orderVolume: '',
    erpSystem: '', erpOther: '', description: ''
  });
  const [formStatus, setFormStatus] = useState({ submitting: false, success: false, error: false });

  const d = (key) => t(`demoPage.${key}`);

  const countries = [
    { value: 'turkey', label: d('countries.turkey') },
    { value: 'germany', label: d('countries.germany') },
    { value: 'finland', label: d('countries.finland') },
    { value: 'denmark', label: d('countries.denmark') },
    { value: 'netherlands', label: d('countries.netherlands') },
    { value: 'belgium', label: d('countries.belgium') },
    { value: 'austria', label: d('countries.austria') },
    { value: 'france', label: d('countries.france') },
    { value: 'other', label: d('countries.other') },
  ];

  const orderVolumes = [
    { value: 'under500', label: d('orderVolumes.under500') },
    { value: '500-1000', label: d('orderVolumes.500to1000') },
    { value: '1000-5000', label: d('orderVolumes.1000to5000') },
    { value: '5000-10000', label: d('orderVolumes.5000to10000') },
    { value: '10000+', label: d('orderVolumes.10000plus') },
  ];

  const erpSystems = [
    'SAP', 'Microsoft Dynamics', 'Logo', 'Netsis', 'Oracle', 'Odoo',
    { value: 'custom', label: d('erp.custom') },
    { value: 'none', label: d('erp.none') },
    { value: 'other', label: d('erp.other') },
  ];

  const scrollTo = (ref) => ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleVideoClick = () => {
    if (videoUnlocked) return;
    setShowFormModal(true);
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    setFormStatus({ submitting: true, success: false, error: false });

    const selectedCountry = countries.find(c => c.value === formData.country);
    const countryLabel = formData.country === 'other'
      ? formData.countryOther
      : (selectedCountry ? selectedCountry.label : formData.country);
    const selectedVolume = orderVolumes.find(v => v.value === formData.orderVolume);
    let erpLabel = formData.erpSystem;
    if (formData.erpSystem === 'other') {
      erpLabel = formData.erpOther;
    } else {
      const erpObj = erpSystems.find(erp => (typeof erp === 'string' ? erp : erp.value) === formData.erpSystem);
      if (erpObj) erpLabel = typeof erpObj === 'string' ? erpObj : erpObj.label;
    }

    const templateParams = {
      companyName: formData.companyName,
      contactName: formData.contactName,
      email: formData.email,
      phone: formData.phone || '-',
      country: countryLabel,
      orderVolume: selectedVolume ? selectedVolume.label : formData.orderVolume,
      erpSystem: erpLabel,
      description: formData.description || '-'
    };

    emailjs.send(EMAILJS_SERVICE_ID, EMAILJS_TEMPLATE_ID, templateParams, EMAILJS_PUBLIC_KEY)
      .then(() => {
        setFormStatus({ submitting: false, success: true, error: false });
        setVideoUnlocked(true);
        setFormData({
          companyName: '', contactName: '', email: '', phone: '',
          country: '', countryOther: '', orderVolume: '',
          erpSystem: '', erpOther: '', description: ''
        });
        setTimeout(() => setShowFormModal(false), 2000);
      })
      .catch(() => {
        setFormStatus({ submitting: false, success: false, error: true });
      });
  };

  return (
    <>
      <SEO titleKey="demoPage.seo.title" descriptionKey="demoPage.seo.description" />

      {/* ─── FULL FORM MODAL (VIDEO GATE) ─── */}
      {showFormModal && (
        <ModalOverlay onClick={() => setShowFormModal(false)}>
          <ModalCard onClick={(e) => e.stopPropagation()}>
            <ModalClose onClick={() => setShowFormModal(false)}><FaTimes /></ModalClose>

            {formStatus.success ? (
              <SuccessCard>
                <FaCheckCircle />
                <h3>{d('success.title')}</h3>
                <p>{d('success.text')}</p>
              </SuccessCard>
            ) : (
              <form onSubmit={handleFormSubmit}>
                <FormTitle>{d('form.title')}</FormTitle>
                <FormSubtitle>{d('form.subtitle')}</FormSubtitle>

                {formStatus.error && <ErrorMessage>{d('form.error')}</ErrorMessage>}

                <FormRow $cols={2}>
                  <FormGroup>
                    <Label htmlFor="m_companyName">{d('form.companyName')} <span>*</span></Label>
                    <Input type="text" id="m_companyName" name="companyName"
                      placeholder={d('form.companyNamePlaceholder')}
                      value={formData.companyName} onChange={handleChange} required />
                  </FormGroup>
                  <FormGroup>
                    <Label htmlFor="m_contactName">{d('form.contactName')} <span>*</span></Label>
                    <Input type="text" id="m_contactName" name="contactName"
                      placeholder={d('form.contactNamePlaceholder')}
                      value={formData.contactName} onChange={handleChange} required />
                  </FormGroup>
                </FormRow>

                <FormRow $cols={2}>
                  <FormGroup>
                    <Label htmlFor="m_email">{d('form.email')} <span>*</span></Label>
                    <Input type="email" id="m_email" name="email"
                      placeholder={d('form.emailPlaceholder')}
                      value={formData.email} onChange={handleChange} required />
                    <FieldHint>{d('form.emailHint')}</FieldHint>
                  </FormGroup>
                  <FormGroup>
                    <Label htmlFor="m_phone">{d('form.phone')}</Label>
                    <Input type="tel" id="m_phone" name="phone"
                      placeholder={d('form.phonePlaceholder')}
                      value={formData.phone} onChange={handleChange} />
                  </FormGroup>
                </FormRow>

                <FormRow $cols={2}>
                  <FormGroup>
                    <Label htmlFor="m_country">{d('form.country')} <span>*</span></Label>
                    <Select id="m_country" name="country"
                      value={formData.country} onChange={handleChange} required>
                      <option value="">{d('form.countryPlaceholder')}</option>
                      {countries.map((c) => (
                        <option key={c.value} value={c.value}>{c.label}</option>
                      ))}
                    </Select>
                    {formData.country === 'other' && (
                      <Input type="text" name="countryOther"
                        placeholder={d('form.countryOtherPlaceholder')}
                        value={formData.countryOther} onChange={handleChange}
                        required style={{ marginTop: '0.5rem' }} />
                    )}
                  </FormGroup>
                  <FormGroup>
                    <Label htmlFor="m_orderVolume">{d('form.orderVolume')} <span>*</span></Label>
                    <Select id="m_orderVolume" name="orderVolume"
                      value={formData.orderVolume} onChange={handleChange} required>
                      <option value="">{d('form.orderVolumePlaceholder')}</option>
                      {orderVolumes.map((v) => (
                        <option key={v.value} value={v.value}>{v.label}</option>
                      ))}
                    </Select>
                  </FormGroup>
                </FormRow>

                <FormRow>
                  <FormGroup>
                    <Label htmlFor="m_erpSystem">{d('form.erpSystem')} <span>*</span></Label>
                    <Select id="m_erpSystem" name="erpSystem"
                      value={formData.erpSystem} onChange={handleChange} required>
                      <option value="">{d('form.erpPlaceholder')}</option>
                      {erpSystems.map((erp) => {
                        const val = typeof erp === 'string' ? erp : erp.value;
                        const label = typeof erp === 'string' ? erp : erp.label;
                        return <option key={val} value={val}>{label}</option>;
                      })}
                    </Select>
                    {formData.erpSystem === 'other' && (
                      <Input type="text" name="erpOther"
                        placeholder={d('form.erpOtherPlaceholder')}
                        value={formData.erpOther} onChange={handleChange}
                        required style={{ marginTop: '0.5rem' }} />
                    )}
                  </FormGroup>
                </FormRow>

                <FormRow>
                  <FormGroup>
                    <Label htmlFor="m_description">{d('form.description')}</Label>
                    <TextArea id="m_description" name="description"
                      placeholder={d('form.descriptionPlaceholder')}
                      value={formData.description} onChange={handleChange} />
                  </FormGroup>
                </FormRow>

                <SubmitButton type="submit" disabled={formStatus.submitting}>
                  {formStatus.submitting ? d('form.submitting') : d('form.submitButton')}
                  {!formStatus.submitting && <FaArrowRight />}
                </SubmitButton>

                <TrustBadgesRow>
                  <TrustBadge><FaCalendarAlt /> {t('trustBadges.onboarding')}</TrustBadge>
                  <TrustBadge><FaTimesCircle /> {t('trustBadges.cancel')}</TrustBadge>
                  <TrustBadge><FaFileContract /> {t('trustBadges.noContract')}</TrustBadge>
                </TrustBadgesRow>
              </form>
            )}
          </ModalCard>
        </ModalOverlay>
      )}

      <PageContainer>
        {/* ─── HERO ─── */}
        <HeroSection>
          <HeroInner>
            <HeroBadge>{d('hero.badge')}</HeroBadge>
            <HeroTitle>{d('hero.title')}</HeroTitle>
            <HeroSubtitle>{d('hero.subtitle')}</HeroSubtitle>
            <HeroButtons>
              <HeroBtn $primary onClick={() => { scrollTo(videoRef); setTimeout(handleVideoClick, 500); }}>
                <FaPlay /> {d('hero.watchVideo')}
              </HeroBtn>
              <HeroBtn $secondary onClick={() => scrollTo(bookRef)}>
                <FaCalendarAlt /> {d('hero.bookDemo')}
              </HeroBtn>
            </HeroButtons>
            <HeroTrust>
              <HeroTrustItem><FaCheck /> {t('trustBadges.onboarding')}</HeroTrustItem>
              <HeroTrustItem><FaCheck /> {t('trustBadges.cancel')}</HeroTrustItem>
              <HeroTrustItem><FaCheck /> {t('trustBadges.noContract')}</HeroTrustItem>
            </HeroTrust>
          </HeroInner>
        </HeroSection>

        {/* ─── VIDEO (GATED WITH FULL FORM) ─── */}
        <VideoSection ref={videoRef}>
          <VideoInner>
            <SectionBadge>{d('videoSection.badge')}</SectionBadge>
            <SectionTitle>{d('videoSection.title')}</SectionTitle>
            <SectionSubtitle>{d('videoSection.subtitle')}</SectionSubtitle>
            <VideoGrid>
              <VideoEmbed onClick={handleVideoClick}>
                <PlayButton><FaPlay /></PlayButton>
                <VideoPlaceholderText>
                  {videoUnlocked ? d('videoSection.videoPlaceholder') : d('hero.watchVideo')}
                </VideoPlaceholderText>
              </VideoEmbed>
              <VideoFeatures>
                {[1, 2, 3, 4, 5, 6].map(num => (
                  <VideoFeatureItem key={num}>
                    <FeatureIcon>{featureIcons[num - 1]}</FeatureIcon>
                    <FeatureText>{d(`videoSection.features.${num}`)}</FeatureText>
                  </VideoFeatureItem>
                ))}
              </VideoFeatures>
            </VideoGrid>
          </VideoInner>
        </VideoSection>


        {/* ─── SOCIAL PROOF ─── */}
        <ProofSection>
          <ProofInner>
            <ProofTitle>{d('socialProof.title')}</ProofTitle>
            <ProofSubtitle>{d('socialProof.subtitle')}</ProofSubtitle>
            <LogoRow>
              <img src={pasaLogo} alt="Pasa Lebensmittel" loading="lazy" />
              <img src={cbGrosshandelLogo} alt="CB Grosshandel" loading="lazy" />
              <img src={belenGemuseLogo} alt="Belen Gemüse" loading="lazy" />
              <img src={dosteliLogo} alt="Dosteli Food" loading="lazy" />
            </LogoRow>
          </ProofInner>
        </ProofSection>

        {/* ─── BOOK A DEMO (CALENDLY) ─── */}
        <BookSection ref={bookRef}>
          <BookInner>
            <BookBadge>{d('bookSection.badge')}</BookBadge>
            <BookTitle>{d('bookSection.title')}</BookTitle>
            <BookSubtitle>{d('bookSection.subtitle')}</BookSubtitle>
            <ApproachRow>
              <ApproachChip><FaCheck /> {d('bookSection.approach1')}</ApproachChip>
              <ApproachChip><FaCheck /> {d('bookSection.approach2')}</ApproachChip>
              <ApproachChip><FaCheck /> {d('bookSection.approach3')}</ApproachChip>
            </ApproachRow>
            <CalendlyButton href={d('bookSection.calendlyUrl')} target="_blank" rel="noopener noreferrer">
              <FaCalendarAlt /> {d('bookSection.calendlyButton')}
            </CalendlyButton>
            <CalendlyNote>{d('bookSection.calendlyNote')}</CalendlyNote>
          </BookInner>
        </BookSection>
      </PageContainer>
    </>
  );
};

export default DemoPage;
